import { z } from '@customerio/hydra-validation';
import type { Account as UIAPIAccount } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * The account record. Wire shape passes through from the generated `Account`
 * model; only the fields the account-settings screens read are asserted at
 * parse time (everything else passes through via `looseObject`).
 *
 * Unlike the augmented auth account (`@/services/auth`), this carries the
 * billing-contact email fields (`invoice_email`, `legal_email`,
 * `technical_email`) and the base `email`, which the settings pages display
 * and edit.
 */
export type Account = UIAPIAccount;

const AccountFromApi = readSchema<Account>(
  z.looseObject({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    invoice_email: z.string(),
    legal_email: z.string(),
    technical_email: z.string(),
    logo_url: z.string(),
    address_line1: z.string(),
    address_line2: z.string(),
    city: z.string(),
    state: z.string(),
    zip_code: z.string(),
    country: z.string(),
    // Security-settings fields (the `/settings/security` page reads these). The
    // backend always sends them, but they're read on a user-facing page, so a
    // missing/malformed value falls back rather than throwing and blanking the page.
    sso_setting: z.string().catch('off'),
    two_factor_authentication_required: z.boolean().catch(false),
    session_duration: z.number().catch(0),
    max_session_duration_hours: z.number().nullish(),
  }),
);

/** @public */
export const account = defineEntity<Account, Account, void, undefined, 'account'>({
  name: 'account',
  scope: 'account',
  pk: (a) => a.id,
  schema: { read: AccountFromApi },
});

export const accountKeys = account.keys;

/** The account switcher's list of the user's account memberships, which predates the account entity. */
export const membershipKeys = {
  all: () => ['getMemberships'] as const,
  list: () => [...membershipKeys.all(), {}] as const,
};

/**
 * Query key for the partner-provisioned accounts list. Lives here (not in
 * queries.ts) so the create mutation can share it without importing queries,
 * which would couple the two modules and break tests that mock them separately.
 */
export function partnerAccountsQueryKey(accountId: number) {
  return ['getPartnerAccounts', { accountId }] as const;
}

export function parseAccount(raw: UIAPIAccount): Account {
  return account.parse(raw);
}
